import { mapState, mapActions, mapGetters } from 'vuex';
import stickyConstants from '@/helpers/stickyConstants';
import marketFiltersMobile from '@/components/market/market-filters/market-filters-mobile/market-filters-mobile.vue';
import marketSectionHeader from '@/components/market/market-section-header/market-section-header.vue';
import marketListDesktopHeader from '@/components/market/market-list-desktop-header/market-list-desktop-header.vue';
import marketListItemV2 from '@/components/market/v2/market-list-item-v2/market-list-item-v2.vue';
import infiniteLoading from 'vue-infinite-loading';
import headerTagsMixin from '@/mixins/headerTags';
import userAgentMixin from '@/mixins/userAgent';
import constants from '@/constants';
import log from '@/logger';

const CLASS_IS_LOADING_DESKTOP = 'is-loading-small';
const CLASS_IS_LOADING_MOBILE = 'is-loading';

export default {
  mixins: [
    headerTagsMixin,
    userAgentMixin,
  ],
  components: {
    infiniteLoading,
    marketFiltersMobile,
    marketSectionHeader,
    marketListDesktopHeader,
    marketListItem: marketListItemV2,
  },
  props: {
    notificationText: {
      type: String,
      required: false,
    },
    notificationLevel: {
      type: String,
      required: false,
    },
    headerId: Number,
    headerUrl: String,
  },
  data() {
    return {
      isLoading: true,
      isFiltering: false,
      infiniteLoader: null,
      filtersUsed: false,
      stickyOptions: stickyConstants.mobile,
      watchersDisabled: false,
    };
  },
  metaInfo() {
    if (this.currentHeader !== null) {
      return {
        title: this.currentHeader.seoTitle || 'Markets',
        link: [
          { rel: 'canonical', href: `${window.location.href}` },
        ],
        meta: [
          { name: 'description', content: this.currentHeader.seoDescription },
          { itemprop: 'name', content: this.currentHeader.seoTitle },
          { itemprop: 'description', content: this.currentHeader.seoDescription },
          { property: 'og:title', content: this.currentHeader.seoTitle },
          { property: 'og:type', content: 'website' },
          { property: 'og:description', content: this.currentHeader.seoDescription },
          { property: 'og:site_name', content: 'Predictit' },
          { property: 'og:url', content: `${window.location.href}` },
        ],
      };
    }
    return {
      title: 'Markets',
    };
  },
  computed: {
    ...mapState('browse', [
      'selectedFilters',
      'selectedOrder',
      'selectedTimespan',
      'filteredMarkets',
      'headers',
      'latestHeaderId',
    ]),
    ...mapState('user', ['marketDisplay']),
    ...mapGetters('app', ['isLayoutDesktop', 'isLayoutMobile']),
    ...mapGetters('user', ['isLoggedIn']),
    ...mapState('app', ['previousRoute']),
    items() {
      const markets = this.filteredMarkets.data === null ? [] : this.filteredMarkets.data;
      const statuses = [
        constants.MARKET_SETTLED,
        constants.MARKET_EXPIRED,
        constants.MARKET_SUSPENDED,
      ];
      return markets
        .filter(m => !statuses.includes(m.status));
    },
    showNoMarketsMessage() {
      return !this.isLoading && this.items.length === 0;
    },
    currentHeader() {
      const header = this.headers ? this.headers.find(h => h.headerId === this.headerId) : null;
      return header || null;
    },
    currentHeaderName() {
      const header = this.currentHeader;
      return header ? header.name : 'All Markets';
    },
    showBiggestGains() {
      return this.isLayoutMobile && !this.filtersUsed;
    },
    sectionHeaderClass() {
      return {
        'page-markets__section-header': true,
        'page-markets__section-header--padding': true,
      };
    },
    filtering() {
      return this.selectedFilters.toString() + this.selectedOrder.name + this.selectedTimespan.name;
    },
  },
  methods: {
    ...mapActions('browse', [
      'clearFilters',
      'clearTimespan',
      'clearOrder',
      'getFilters',
      'getFilteredMarkets',
      'setLatestHeaderId',
    ]),
    ...mapActions('user', ['updateMarketDisplay']),
    ...mapActions('notifications', ['setSideNotification']),
    fetchData() {
      this.isLoading = true;
      this.infiniteLoader = null;
      if (this.isFiltering) return;
      if (Number.isInteger(this.headerId)) {
        log.info('[MARKETS] Fetching Data');
        this.isFiltering = true;
        this.reorganizeTags();
        this.getFilters(this.headerId)
          .then(() => {
            this.getMarkets(true)
              .then(() => {
                this.isFiltering = false;
                this.isLoading = false;
              })
              .catch(() => {
                this.isLoading = false;
                this.isFiltering = false;
              });
          })
          .catch(() => {
            this.isFiltering = false;
            this.isLoading = false;
          });
      }
    },
    getMarkets(reset = false) {
      return this.getFilteredMarkets({
        headerId: this.headerId || 0,
        reset,
      });
    },
    onLayoutToggle(layout) {
      this.updateMarketDisplay(layout);
    },
    onFiltersUsed() {
      this.filtersUsed = true;
    },
    filteringChanged() {
      if (this.watchersDisabled) return;

      log.info(`[MARKETS] Filtering. Is in Progress: ${this.isFiltering.toString()}`);
      if (this.isFiltering) return;
      this.$router.push({
        name: 'market-list',
        params: {
          headerId: this.headerId,
          headerUrl: this.headerUrl,
        },
        query: {
          filterIds: this.selectedFilters.join(','),
          sort: this.selectedOrder.sort,
          sortParameter: this.selectedTimespan.parameter,
        },
      });
      this.fetchData();
    },
    headerIdChanged(newValue, oldValue) {
      log.info('[MARKETS] headerId Changed', this.latestHeaderId, oldValue, newValue);

      const shouldClearHeaderData = (typeof oldValue !== 'undefined'
        && oldValue !== newValue
        && (this.latestHeaderId !== newValue)) || (newValue === 0 && this.previousRoute);

      if (shouldClearHeaderData) {
        // this.clearOrder();
        // this.clearTimespan();
        this.setLatestHeaderId(newValue);
        const location = window.location.href;
        if (!location.includes('widget')) {
          log.info('[MARKETS] Clearing Filters');
          this.clearFilters();
        }
      }
      this.filtersUsed = false;
      this.fetchData();
    },
    goInfinite(infi) {
      this.infiniteLoader = infi;

      if (this.filteredMarkets.canLoadMore) {
        this.getMarkets(false);
      }
    },

  },
  watch: {
    headerId: {
      handler: 'headerIdChanged',
      immediate: true,
    },
    filtering: 'filteringChanged',
    isLoggedIn: 'fetchData',
    isFiltering(value) {
      const target = (this.isLayoutDesktop) ?
        this.$refs.desktopHeader.$el :
        this.$refs.mobileHeader.$el;
      const targetClass = (this.isLayoutDesktop) ?
        CLASS_IS_LOADING_DESKTOP :
        CLASS_IS_LOADING_MOBILE;
      if (value) {
        target.classList.add(targetClass);
      } else {
        target.classList.remove(targetClass);
      }
    },
    items() {
      if (this.infiniteLoader) {
        this.infiniteLoader.loaded();
        if (!this.filteredMarkets.canLoadMore) {
          this.infiniteLoader.complete();
        }
      }
    },
  },
  created() {
    if (this.notificationText && this.notificationLevel) {
      this.setSideNotification({
        text: this.notificationText,
        level: this.notificationLevel,
      });
    }
  },
  beforeRouteLeave(to, from, next) {
    this.watchersDisabled = true;
    if (to.name !== 'market-details') {
      this.clearFilters();
    }
    next();
  },
};



// WEBPACK FOOTER //
// ./src/components/pages/page-markets/page-markets.js